/** Money helpers. All arithmetic goes through these — never raw floats. */

import {
  CURRENCY_DISPLAY,
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_DISPLAY,
  type Locale,
} from "../constants/config";

export const MONEY_SCALE = 2 as const;
const MONEY_FACTOR = 10 ** MONEY_SCALE;

/** Rounds to 2 decimals, tolerating binary float drift (e.g. 0.1 + 0.2). */
export const roundMoney = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * MONEY_FACTOR) / MONEY_FACTOR;
};

export const sumMoney = (values: readonly number[]): number =>
  roundMoney(values.reduce((total, value) => total + value, 0));

export const toMinorUnits = (value: number): number => Math.round(roundMoney(value) * MONEY_FACTOR);

export const fromMinorUnits = (minor: number): number => roundMoney(minor / MONEY_FACTOR);

export const percentOf = (amount: number, percent: number): number =>
  roundMoney((amount * percent) / 100);

export const clampMoney = (value: number, min = 0, max = Number.MAX_SAFE_INTEGER): number =>
  roundMoney(Math.min(Math.max(value, min), max));

export const isPositiveMoney = (value: number): boolean => Number.isFinite(value) && roundMoney(value) > 0;

export const moneyEquals = (a: number, b: number): boolean => toMinorUnits(a) === toMinorUnits(b);

const fallbackFormat = (amount: number): string =>
  roundMoney(amount).toLocaleString(DEFAULT_CURRENCY_DISPLAY.locale, {
    minimumFractionDigits: DEFAULT_CURRENCY_DISPLAY.fractionDigits,
    maximumFractionDigits: DEFAULT_CURRENCY_DISPLAY.fractionDigits,
  });

/** `formatMoney(150000)` → `Rp 150.000` (IDR) — never throws on exotic input. */
export const formatMoney = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  locale?: Locale,
): string => {
  const display = CURRENCY_DISPLAY[currency] ?? DEFAULT_CURRENCY_DISPLAY;
  try {
    return new Intl.NumberFormat(locale ?? display.locale, {
      style: "currency",
      currency,
      minimumFractionDigits: display.fractionDigits,
      maximumFractionDigits: display.fractionDigits,
    }).format(roundMoney(amount));
  } catch {
    return fallbackFormat(amount);
  }
};

/** Plain grouped number, used inside invoice tables where the symbol is shown once. */
export const formatAmount = (amount: number, locale?: Locale): string => {
  const display = DEFAULT_CURRENCY_DISPLAY;
  try {
    return new Intl.NumberFormat(locale ?? display.locale, {
      minimumFractionDigits: display.fractionDigits,
      maximumFractionDigits: display.fractionDigits,
    }).format(roundMoney(amount));
  } catch {
    return fallbackFormat(amount);
  }
};
